<template>
  <div class="container">
    <el-form :model="form" inline ref="refForm" :rules="rules">
      <el-form-item class="small_label" :label="$t('common.orderNo') + ':'" prop="incoOrderNo">
        <el-input
          v-model.trim="form.incoOrderNo"
          name="incoOrderNo"
          type="text"
          autocomplete="on"
          clearable
          tabindex="1"
          size="small"
          style="width: 200px;"
          maxlength="64"
        />
      </el-form-item>
      <el-form-item class="small_label" :label="$t('common.customerName') + ':'" prop="customerId">
        <el-select
          v-model="form.customerId"
          clearable
          filterable
          size="small"
          style="width: 200px;">
          <el-option
            v-for="item in customersList"
            :key="item.id"
            :label="item.customerName"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item class="small_label" :label="$t('common.productName') + ':'" prop="productName">
        <el-select
          v-model="form.productId"
          clearable
          filterable
          style="width: 200px;"
          size="small">
          <el-option
            v-for="item in productsList"
            :key="item.id"
            :label="item.productName + ' / ' + item.chipName"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item class="small_label" :label="$t('common.lotNo') + ':'" prop="lotNo">
        <el-input
          v-model.trim="form.lotNo"
          name="lotNo"
          type="text"
          autocomplete="on"
          clearable
          tabindex="5"
          size="small"
          style="width: 150px;"
          maxlength="32"
        />
      </el-form-item>
      <el-form-item class="small_label" :label="$t('common.orderTime') + ':'" prop="orderDate">
        <el-date-picker
          v-model="form.orderDate"
          type="daterange"
          size="small"
          clearable
          style="width: 250px;"
          :picker-options="pickerOptions"
          :default-value="defaultTime">
        </el-date-picker>
      </el-form-item>
      <el-form-item>
        <el-button size="small" @click="reset">{{$t('common.reset')}}</el-button>
        <el-button type="primary" size="small" @click="queryOrderRecord">
          {{$t('common.query')}}
        </el-button>
      </el-form-item>
    </el-form>
    <div class="separator"></div>
    <div class="target-container">
      <el-table
        ref="table"
        height="100%"
        border
        :data="displayData"
        :cell-style="{textAlign: 'center', padding: '8px 0', fontSize: '12px'}"
        :header-cell-style="{textAlign: 'center', padding: '5px 0', fontSize: '12px', color: '#303133', fontFamily: 'SimHei'}"
        header-cell-class-name="headerCellName"
        :row-key="getRowKeys"
        @selection-change="handleSelectionChange"
        :span-method="objectSpanMethod">
        <el-table-column
          type="selection"
          width="55"
          :reserve-selection="true">
        </el-table-column>
        <el-table-column fixed prop="incoOrderNo" :label="$t('common.orderNo')"/>
        <el-table-column :label="$t('common.customerName')">
          <template slot-scope="scope">
            {{scope.row.customer && scope.row.customer.customerName}}
          </template>
        </el-table-column>
        <el-table-column prop="subNo" :label="$t('common.subNo')"/>
        <el-table-column :label="$t('common.productName')" width="200">
          <template slot-scope="scope">
            {{productName(scope.row)}}
          </template>
        </el-table-column>
        <el-table-column prop="lotNo" :label="$t('common.lotNo')"/>
        <el-table-column :label="$t('common.processType')" width="100" >
          <template slot-scope="scope">
            {{processTypeVal(scope.row.processType)}}
          </template>
        </el-table-column>
        <el-table-column prop="count" :label="$t('common.count')" width="100" />
        <el-table-column :label="$t('common.storePassCount')" width="100">
          <template slot-scope="scope">
            {{scope.row.passCount - scope.row.sendPassCount}}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.storeFailCount')" width="100">
          <template slot-scope="scope">
            {{scope.row.failCount - scope.row.sendFailCount}}
          </template>
        </el-table-column>
        <el-table-column width="100" :label="$t('incomingOrder.sendCount')">
          <template slot-scope="scope">
            {{scope.row.sendFailCount + scope.row.sendPassCount}}
          </template>
        </el-table-column>
        <el-table-column v-for="(row, key) in 8" :label="$t('common.binLevel') + row" :key="key">
          <template slot-scope="scope">
            {{scope.row.inStoreCount && scope.row.inStoreCount['binlevel' + row]}}
          </template>
        </el-table-column>
        <el-table-column fixed="right" width="100" :label="$t('common.orderTime')">
          <template slot-scope="scope">
            {{formatDate(scope.row.orderDate)}}
          </template>
        </el-table-column>
      </el-table>
    </div>
    <pagination
      :total="totalCount"
      :pageSize="perpage"
      :current-page.sync="page"
      @current-change="onChangePage"
    />
    <div class="control-container">
      <span style="color: #404040; font-size: 14px;">{{$t('deliveryGoods.selected')}}<span style="color: #EC7900; font-size: 24px;">{{selectedOrderCount}}</span>{{$t('deliveryGoods.orders')}}</span>
      <span>
        <el-button @click="prevStep" size="small" class="prev-button">
          {{$t('common.prevStep')}}
        </el-button>
        <el-button @click="sureBuild" size="small" class="sure-button">
          {{$t('common.nextStep')}}
        </el-button>
      </span>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { processTypes, storeStatus } from '@/utils/defines';
import { incoOrderList } from '@/api/incoOrder';
import { customersList } from '@/api/customers';
import { productsList } from '@/api/products';
import {
  Pagination,
} from '@/components';

const defaultForm = {
  incoOrderNo: undefined,
  customerId: undefined,
  productId: undefined,
  lotNo: undefined,
  orderDate: undefined,
};

export default {
  name: 'ChooseOrder',
  components: {
    Pagination,
  },
  data() {
    const processTypesObj = {};
    let processTypeItem;
    for (const key in processTypes) {
      if (Object.prototype.hasOwnProperty.call(processTypes, key)) {
        processTypeItem = processTypes[key];
        processTypesObj[processTypeItem.val] = processTypeItem.explain;
      }
    }
    const storeStatusObj = {};
    let storeStatueItem;
    for (const key in storeStatus) {
      if (Object.prototype.hasOwnProperty.call(storeStatus, key)) {
        storeStatueItem = storeStatus[key];
        storeStatusObj[storeStatueItem.val] = storeStatueItem.prodOderExplain || storeStatueItem.explain;
      }
    }
    return {
      form: Object.assign({}, defaultForm),
      searchParams: Object.assign({}, defaultForm),
      rules: {
      },
      page: 1,
      perpage: 20,
      totalCount: 0,
      listData: [],
      customersList: [],
      productsList: [],
      processTypesObj,
      storeStatusObj,
      multipleSelection: [],
    };
  },
  computed: {
    displayData() {
      return this.listData;
    },
    pageCount() {
      return Math.ceil(this.totalCount / this.perpage);
    },
    pickerOptions() {
      return {
        disabledDate(time) {
          return time.getTime() > moment().add(1, 'days').valueOf();
        }
      };
    },
    defaultTime() {
      return moment().subtract(1, 'months').valueOf();
    },
    selectedOrderCount() {
      const multipleSelection = this.multipleSelection;
      let count = 0;
      for (let row of multipleSelection) {
        if (row.rowspan) {
          count++;
        }
      }
      return count;
    },
  },
  mounted() {
    this.incoOrderList();
    this.customersListGet(1, 200);
    this.productsListGet(1, 200);
  },
  methods: {
    getRowKeys(row) {
      return row.id;
    },
    onChangePage() {
      this.incoOrderList();
    },
    async incoOrderList() {
      try {
        const {
          incoOrderNo,
          customerId,
          productId,
          lotNo,
          orderDate,
        } = this.searchParams;
        let orderStartDate;
        let orderEndDate;
        if (orderDate && orderDate.length === 2) {
          [orderStartDate, orderEndDate] = orderDate;
        }
        const res = await incoOrderList({
          page: this.page,
          perpage: this.perpage,
          incoOrderNo: incoOrderNo || undefined,
          customerId: customerId || undefined,
          productId: productId || undefined,
          lotNo: lotNo || undefined,
          orderStartDate,
          orderEndDate,
          status: storeStatus.canbeDelivered.val,
        });
        const { data } = res;
        if (data.count !== undefined) {
          this.totalCount = data.count;
        }
        this.listData = [];
        let incoOrdersDetails, incoOrdersDetailsItem, length, deliveryNumber = 0;
        data.records.map(item => {
          incoOrdersDetails = item.incoOrdersDetails;
          length = incoOrdersDetails.length;
          deliveryNumber = 0;
          for (let i = 0; i < length; i++) {
            incoOrdersDetailsItem = incoOrdersDetails[i];
            deliveryNumber = deliveryNumber
              + incoOrdersDetailsItem.passCount + incoOrdersDetailsItem.failCount
              - incoOrdersDetailsItem.sendFailCount - incoOrdersDetailsItem.sendPassCount;
            if (i === 0) {
              this.listData.push({
                ...item,
                ...incoOrdersDetails[i],
                status: item.status,
                rowspan: length
              });
            } else {
              this.listData.push({
                ...item,
                ...incoOrdersDetails[i],
                status: item.status,
              });
            }
            if (i === length - 1) {
              this.listData[this.listData.length - length].deliveryNumber = deliveryNumber;
            }
          }
        });
      } catch (e) {
        // do nothing.
      }
    },
    async customersListGet(page, perpage, customerName) {
      try {
        const res = await customersList({ page, perpage, customerName });
        const { data } = res;
        this.customersList = data.records;
      } catch (e) {
        // do nothing.
      }
    },
    async productsListGet(page, perpage) {
      try {
        const res = await productsList({ page, perpage });
        const { data } = res;
        this.productsList = data.records;
      } catch (e) {
        // do nothing.
      }
    },
    reset() {
      this.form = Object.assign({}, defaultForm);
      this.searchParams = Object.assign({}, defaultForm);
      this.incoOrderList();
    },
    queryOrderRecord() {
      this.$refs.table.clearSelection();
      this.multipleSelection = [];
      this.page = 1;
      this.searchParams = {...this.form};
      this.incoOrderList();
    },
    productName(row) {
      const { product } = row;
      if (!product) {
        return '';
      }
      return `${product.productName} / ${product.chipName}`;
    },
    processTypeVal(val) {
      return this.processTypesObj[val];
    },
    storeStatusVal(val) {
      return this.storeStatusObj[`${val}`];
    },
    formatDate(date) {
      if (!date) {
        return '';
      }
      const updateDate = moment.utc(date);
      const isSameYear = updateDate.local().format('YYYY') === moment.utc().local().format('YYYY');
      if (isSameYear) {
        return updateDate.local().format('MM/DD');
      }
      return updateDate.local().format('YYYY/MM/DD');
    },
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    objectSpanMethod({ row, column, rowIndex, columnIndex }) {
      if (columnIndex === 0 || columnIndex === 1 || columnIndex === 2) {
        if (row.rowspan) {
          return {
            rowspan: row.rowspan,
            colspan: 1,
          };
        }
        return {
          rowspan: 0,
          colspan: 0,
        };
      }
    },
    prevStep() {
      this.$emit('prevStep');
    },
    sureBuild() {
      if (this.multipleSelection.length === 0) {
        this.$message({
          type: 'info',
          message: this.$t('deliveryGoods.selectTheOrderFirst'),
        });
        return;
      }
      const selectOrder = [];
      let incoOrdersDetails, length;
      for (let item of this.multipleSelection) {
        if (item.rowspan) {
          incoOrdersDetails = item.incoOrdersDetails;
          length = incoOrdersDetails.length;
          for (let i = 0; i < length; i++) {
            if (i === 0) {
              selectOrder.push({
                ...item,
                ...incoOrdersDetails[i],
                status: item.status,
                rowspan: length,
              });
            } else {
              selectOrder.push({
                ...item,
                ...incoOrdersDetails[i],
                status: item.status,
                rowspan: undefined,
              });
            }
          }
        }
      }
      this.$emit('nexStep');
      this.$emit('select', selectOrder);
    },
  },
};
</script>

<style lang="scss" scoped>
  .container {
    height: 100%;
    display: -webkit-flex;
    display: flex;
    flex-direction: column;
  }
  .el-form {
    padding: 10px 20px 0;
  }
  .el-form-item {
    margin-bottom: 10px;
  }
  .separator {
    width: 100%;
    height: 1px;
    background: #E5E5E5;
    display: block;
  }
  .control-container {
    background: #F5F5F5;
    padding: 20px;
    display: flex;
    display: -webkit-flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .target-container {
    flex: 1;
    display: -webkit-flex;
    display: flex;
    margin: 20px 20px 0;
    height: 100%;
    overflow: auto;
  }
  .bottom-container {
    text-align: center;
    position: relative;
    margin: 20px auto;
    width: 100%;
  }
  .link {
    color: #169BD5;
    padding: 0 5px;
  }
  .prev-button {
    background: white;
    color: #EC7900;
    border: #EC7900 solid 1px;
  }
  .sure-button {
    background: #EC7900;
    color: white;
  }
</style>
<style>
  .headerCellName {
    background: -webkit-linear-gradient(#F9F9F9, #EEEEEE);
    background: -o-linear-gradient(#F9F9F9, #EEEEEE);
    background: -moz-linear-gradient(#F9F9F9, #EEEEEE);
    background: linear-gradient(#F9F9F9, #EEEEEE);
  }
  .small_label .el-form-item__label {
    font-size: 12px;
  }
</style>
